import React, { useState, FormEvent } from 'react';
import { auth } from '../../services/firebase';
import { signInWithEmailAndPassword, User, IdTokenResult, AuthError } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { FaEye, FaEyeSlash, FaUserShield } from 'react-icons/fa';

interface AdminClaims {
  [truckId: string]: boolean;
}

interface CustomIdTokenResult extends IdTokenResult {
  claims: {
    admin?: AdminClaims;
    [key: string]: any;
  };
}

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();

  const verifyAdminClaims = async (user: User): Promise<boolean> => {
    const idTokenResult = await user.getIdTokenResult() as CustomIdTokenResult;
    const adminClaims = idTokenResult.claims.admin;

    if (adminClaims && typeof adminClaims === 'object') {
      return Object.values(adminClaims).some(value => value === true);
    }
    return false;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Force token refresh to ensure latest claims
      await user.getIdToken(true);
      
      const hasAdminAccess = await verifyAdminClaims(user);
      
      if (hasAdminAccess) {
        navigate('/');
      } else {
        setError("Vous n'avez pas les droits d'administration nécessaires.");
        await auth.signOut();
      }
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage: string;

      if (error instanceof Error && 'code' in error) {
        const authError = error as AuthError;
        switch (authError.code) {
          case 'auth/user-not-found':
            errorMessage = 'Aucun utilisateur trouvé avec cet email';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Mot de passe invalide';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Ce compte a été désactivé';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Trop de tentatives échouées. Veuillez réessayer plus tard.';
            break;
          default:
            errorMessage = 'Échec de la connexion. Veuillez vérifier vos identifiants.';
        }
      } else {
        errorMessage = 'Une erreur inattendue est survenue';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      py: 12,
      px: { xs: 2, sm: 3, lg: 4 }
    }}>
      <Paper elevation={3} sx={{
        maxWidth: 480,
        width: '100%',
        p: 4,
        borderRadius: 3
      }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}>
            <FaUserShield size={28} color="white" />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            Administration
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary'
            }}
          >
            Connectez-vous à votre portail d'administration
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            id="email-address"
            name="email"
            type="email"
            label="Adresse e-mail"
            placeholder="Entrez votre adresse e-mail"
            autoComplete="email"
            required
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            label="Mot de passe"
            placeholder="Entrez votre mot de passe"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </IconButton>
                  </InputAdornment>
                )
              }
            }}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                id="remember-me"
                name="remember-me"
                color="primary"
              />
            }
            label="Se souvenir de moi"
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '1rem'
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Connexion en cours...
              </>
            ) : (
              'Se connecter'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;